import classNames from "classnames";
import React, { ReactNode, useRef, useState } from "react";
import NumberSelector from "../number-selector";
import SelectInput from "../select-input";
import { SliderMultipleValue } from "../slider/slider-multiple-value";
import TextInput from "../text-input";
import { DateInputFieldProps } from "../date-selection/date-input-field/DateInputField.types";
import { SliderMultipleValueObject } from "../slider/slider-multiple-value.types";
import CheckboxFilterInput from "./checkbox-filter-input";
import DateRangeFilterInput from "./date-range-filter-input";
import FilterFooter from "./filter-footer";
import FilterHeader from "./filter-header";
import {
  isCheckboxFilter,
  isCustomMultipleFilter,
  isCustomSingleFilter,
  isDateRangeFilter,
  isNumberSelectFilter,
  isRadioFilter,
  isSelectFilter,
  isSliderMultiValFilter,
  isTextFilter,
  isToggleFilter,
} from "./filter-helper";
import FilterModule from "./filter-module";
import {
  AppliedFilterState,
  AppliedFilterType,
  FilterInputType,
  FilterModuleProps,
  FilterModuleType,
  FilterProps,
  OnClearFilterInternal,
  OnRemoveFilterInternal,
} from "./filter.types";
import MultiFilterWrapper from "./multi-filter-wrapper";
import RadioFilterInput from "./radio-filter-input";
import ToggleFilterInput from "./toggle-filter-input";
import { getAppliedFilterTagsInfo } from "./util/getAppliedFilterTagsInfo";
import { FilterContext } from "./util/filterContext";
import useResizeObserver from "../utils/useResizeObserver";

/*
 * Forcing type assertion for a component due issue with component
 * type being generated as `any` in d.ts file when forwardRef is used.
 * Probably it could be fixed with new ts-loader added (webpack 5 needed)
 */

export const Filter = React.forwardRef<HTMLDivElement, FilterProps>(
  (
    {
      defaultFiltersExpand = false,
      filtersExpand: filtersExpandedProp,
      filterModules = [],
      onShowFilters,
      onHideFilters,
      onShowMoreFilters,
      onShowFewerFilters,
      renderAppliedFiltersHiddenText,
      resultsCount,
      clearAllText,
      minimumModulesCount: minimumModulesCountInp = 4,
      onApplyFilters,
      onRemoveFilter,
      onClearFilter,
      onClearAllFilters,
      applyButtonText,
      withApplyButton = false,
      className,
      isControlled = false,
      footerButtons,
      ...rest
    }: FilterProps,
    filterRef
  ): React.ReactElement => {
    const defaultValues = useRef<AppliedFilterState>({});
    // below state is map that is used to track input values in a map only for uncontrolled inputs
    const [appliedFilterState, _setAppliedFilterState] =
      useState<AppliedFilterState>(() => {
        const map: AppliedFilterState = {};
        filterModules.forEach((module) => {
          const { type, input, name } = module;
          if (isCheckboxFilter(type, input)) {
            map[name] = input.defaultSelectedValues ?? [];
          } else if (isRadioFilter(type, input)) {
            map[name] = input.defaultSelectedValue;
          } else if (isNumberSelectFilter(type, input)) {
            map[name] = input.defaultValue ?? 0;
          } else if (isToggleFilter(type, input)) {
            map[name] = input.defaultValue?.toString();
          } else if (isTextFilter(type, input)) {
            map[name] = input.defaultValue;
          } else if (isSliderMultiValFilter(type, input)) {
            const min = input.defaultMinValue;
            const max = input.defaultMaxValue;
            if (min !== undefined && max !== undefined) {
              map[name] = { min, max };
            }
          } else if (isDateRangeFilter(type, input)) {
            map[name] = [null, null];
          } else if (isSelectFilter(type, input)) {
            map[name] = input.defaultValue;
          }
        });
        defaultValues.current = map;
        return map;
      });

    const setAppliedFilterState = (
      inpState: AppliedFilterState,
      updateTags: boolean
    ): void => {
      _setAppliedFilterState(inpState);
      if (updateTags) {
        setAppliedFilterTagsState(
          getAppliedFilterTagsInfo(filterModules, inpState)
        );
      }
    };

    const { width: windowWidth } = useResizeObserver({
      ref: document.body,
      box: "border-box",
    });
    const isXsScreen = windowWidth ? windowWidth <= 543 : false;

    const minimumModulesCount = isXsScreen
      ? filterModules.length
      : minimumModulesCountInp;

    const [_filterExpanded, setFilterExpanded] = useState(defaultFiltersExpand);
    const filterExpanded = filtersExpandedProp ?? _filterExpanded;
    const [isShowMoreFiltersActive, setIsShowMoreFiltersActive] =
      useState(false);

    const controlledValuesMap: AppliedFilterState = {};

    const updateFilterTagsRef = useRef(false);

    const containerClasses = classNames(
      "spark-filter",
      "spark-filter--initialized",
      {
        "filter-expanded": filterExpanded,
        "spark-filter--dropdown": isXsScreen,
      },
      className
    );

    const handleOnInputChange = (
      module: FilterModuleProps,
      value: AppliedFilterType,
      e?: React.MouseEvent | React.ChangeEvent
    ): void => {
      let map: AppliedFilterState = {};
      updateFilterTagsRef.current = false;
      if (!isControlled) {
        map = appliedFilterState;
        const newState = {
          ...appliedFilterState,
          [module.name]: value,
        };
        setAppliedFilterState(newState, !withApplyButton);
      } else {
        map = controlledValuesMap;
      }
      if (withApplyButton === false) {
        const reqFilterMap = { ...map, [module.name]: value };
        onApplyFilters?.(reqFilterMap, e);
      }
    };

    const _onToggleFiltersClickHandle: React.MouseEventHandler<
      HTMLButtonElement
    > = (e) => {
      if (filterExpanded) {
        setFilterExpanded(false);
        onHideFilters?.(e);
      } else {
        setFilterExpanded(true);
        onShowFilters?.(e);
      }
    };

    const _onToggleMoreFiltersClickHandle: React.MouseEventHandler<
      HTMLButtonElement
    > = (e) => {
      if (isShowMoreFiltersActive) {
        setIsShowMoreFiltersActive(false);
        onShowFewerFilters?.(e);
      } else {
        setIsShowMoreFiltersActive(true);
        onShowMoreFilters?.(e);
      }
    };

    const _onApplyFiltersButtonHandle = (
      e: React.MouseEvent<HTMLButtonElement>
    ): void => {
      setFilterExpanded(false);
      updateFilterTagsRef.current = true;
      setAppliedFilterTagsState(
        getAppliedFilterTagsInfo(
          filterModules,
          isControlled ? controlledValuesMap : appliedFilterState
        )
      );
      onApplyFilters?.(
        isControlled ? controlledValuesMap : appliedFilterState,
        e,
        true
      );
    };

    const getStateOnDeleteFilter = ({
      type,
      input,
      value,
      newState,
      name,
    }: {
      type: FilterModuleType;
      input: FilterInputType;
      value?: string;
      newState: { [x: string]: AppliedFilterType };
      name: string;
    }): AppliedFilterState => {
      if (isCheckboxFilter(type, input)) {
        if (value === undefined) {
          newState = { ...newState, [name]: defaultValues.current[name] ?? [] };
        } else {
          let selectedValues = newState[name] as string[];
          selectedValues = selectedValues.filter((e) => e !== value);
          newState = { ...newState, [name]: selectedValues };
        }
      } else if (isTextFilter(type, input)) {
        newState = { ...newState, [name]: defaultValues.current[name] ?? "" };
      } else if (isDateRangeFilter(type, input)) {
        newState = {
          ...newState,
          [name]: defaultValues.current[name] ?? [null, null],
        };
      } else if (
        isRadioFilter(type, input) ||
        isToggleFilter(type, input) ||
        isNumberSelectFilter(type, input) ||
        isSliderMultiValFilter(type, input) ||
        isSelectFilter(type, input)
      ) {
        newState = { ...newState, [name]: defaultValues.current[name] };
      }
      return newState;
    };

    const onRemoveFilterHandle: OnRemoveFilterInternal = (e, name, value) => {
      const module = filterModules.find((e) => e.name === name);
      if (module) {
        const { type, input } = module;
        let newState = isControlled
          ? { ...controlledValuesMap }
          : { ...appliedFilterState };
        updateFilterTagsRef.current = true;
        newState = getStateOnDeleteFilter({
          type,
          input,
          value,
          newState,
          name,
        });
        if (!isControlled) {
          setAppliedFilterState(newState, true);
        }
        onRemoveFilter?.(e, newState, name, value);
      }
    };

    const onClearFilterHandle: OnClearFilterInternal = (e, name) => {
      const module = filterModules.find((e) => e.name === name);
      if (module) {
        const { type, input } = module;
        let newState = isControlled
          ? { ...controlledValuesMap }
          : { ...appliedFilterState };
        updateFilterTagsRef.current = false;
        newState = getStateOnDeleteFilter({
          type,
          input,
          newState,
          name,
        });
        if (!isControlled) {
          setAppliedFilterState(newState, !withApplyButton);
        }
        onClearFilter?.(e, newState, name);
      }
    };

    const onClearAllFiltersHandle = (
      e: React.MouseEvent<HTMLAnchorElement>
    ): void => {
      let newState: AppliedFilterState = isControlled
        ? { ...controlledValuesMap }
        : { ...appliedFilterState };
      updateFilterTagsRef.current = true;
      filterModules.forEach((module) => {
        if (module) {
          const { type, input, name } = module;
          newState = getStateOnDeleteFilter({
            type,
            input,
            newState,
            name,
          });
        }
      });
      if (!isControlled) {
        setAppliedFilterState(newState, true);
      }
      onClearAllFilters?.(e, newState);
    };

    const filterContentClasses = classNames("spark-filter__content", {
      hide: !filterExpanded,
    });

    const modules: ReactNode[] = filterModules.map((module, index) => {
      let content: ReactNode = <></>;
      const {
        type,
        title: moduleTitle,
        name,
        input,
        isClearBtnEnabled: isClearBtnEnabledInput,
        ...rest
      } = module;

      let isClearBtnEnabled: undefined | boolean = false;
      if (isCheckboxFilter(type, input)) {
        controlledValuesMap[name] = input.selectedValues;
        const value =
          input.selectedValues ?? (appliedFilterState[name] as string[]);
        isClearBtnEnabled = value && value.length > 0;
        content = (
          <CheckboxFilterInput
            {...input}
            selectedValues={value}
            onChange={(e, selectedItems): void => {
              input.onChange?.(e, selectedItems);
              handleOnInputChange(module, selectedItems, e);
            }}
          />
        );
      } else if (isRadioFilter(type, input)) {
        controlledValuesMap[name] = input.selectedValue;
        const value = input.selectedValue ?? appliedFilterState[name];
        isClearBtnEnabled = !!value;
        content = (
          <RadioFilterInput
            {...input}
            selectedValue={value?.toString()}
            onChange={(e, val): void => {
              input.onChange?.(e, val);
              handleOnInputChange(module, val, e);
            }}
          />
        );
      } else if (isNumberSelectFilter(type, input)) {
        controlledValuesMap[name] = input.value;
        const value = input.value ?? (appliedFilterState[name] as number);
        isClearBtnEnabled = !!value;
        content = (
          <NumberSelector
            {...input}
            value={value}
            onChange={(e, val): void => {
              input.onChange?.(e, val);
              handleOnInputChange(module, val, e);
            }}
          />
        );
      } else if (isToggleFilter(type, input)) {
        controlledValuesMap[name] = input.value;
        const value = input.value ?? appliedFilterState[name];
        content = (
          <ToggleFilterInput
            {...input}
            value={value?.toString()}
            onChange={(e, value): void => {
              input.onChange?.(e, value);
              handleOnInputChange(module, value ?? "", e);
            }}
          />
        );
      } else if (isTextFilter(type, input)) {
        controlledValuesMap[name] = input.value;
        const value = input.value ?? appliedFilterState[name];
        isClearBtnEnabled = !!value;
        content = (
          <TextInput
            {...input}
            ref={input.ref as React.Ref<HTMLInputElement>}
            value={value?.toString()}
            onChange={(e, value): void => {
              input.onChange?.(e, value);
              handleOnInputChange(module, value, e);
            }}
          />
        );
      } else if (isSliderMultiValFilter(type, input)) {
        let { minValue, maxValue } = input;
        if (minValue !== undefined && maxValue !== undefined) {
          controlledValuesMap[name] = { min: minValue, max: maxValue };
        }
        if (minValue === undefined || maxValue === undefined) {
          const val = appliedFilterState[name] as SliderMultipleValueObject;
          minValue = val.min;
          maxValue = val.max;
        }
        isClearBtnEnabled = true;
        content = (
          <SliderMultipleValue
            {...input}
            minValue={minValue}
            maxValue={maxValue}
            ref={input.ref as any}
            onChange={({ min, max }): void => {
              input.onChange?.({ min, max });
              handleOnInputChange(module, { min, max });
            }}
          />
        );
      } else if (isDateRangeFilter(type, input)) {
        let range = input.startDate.range || input.endDate.range;
        if (range !== undefined) {
          controlledValuesMap[name] = range;
        } else {
          range = appliedFilterState[name] as DateInputFieldProps["value"][];
        }

        isClearBtnEnabled =
          range && range.length > 0 && !range.every((e) => !e);
        content = (
          <DateRangeFilterInput
            {...input}
            startDate={{
              ...input.startDate,
              range,
              onRangeChange: (_range): void => {
                input.startDate.onRangeChange?.(_range);
                handleOnInputChange(module, _range);
              },
            }}
            endDate={{
              ...input.endDate,
              range,
              onRangeChange: (_range): void => {
                input.endDate.onRangeChange?.(_range);
                handleOnInputChange(module, _range);
              },
            }}
          />
        );
      } else if (isSelectFilter(type, input)) {
        controlledValuesMap[name] = input.value;
        const value = input.value ?? appliedFilterState[name];
        isClearBtnEnabled = true;
        content = (
          <SelectInput
            {...input}
            defaultValue={undefined}
            value={value?.toString()}
            onChange={(e, val): void => {
              input.onChange?.(e, val);
              handleOnInputChange(module, val, e);
            }}
          />
        );
      } else if (isCustomSingleFilter(type, input)) {
        content = input.children;
      } else if (isCustomMultipleFilter(type, input)) {
        content = <MultiFilterWrapper {...input} />;
      } else {
        content = <div></div>;
      }
      isClearBtnEnabled = isClearBtnEnabledInput || isClearBtnEnabled;

      return (
        <FilterModule
          {...rest}
          type={type}
          title={moduleTitle}
          name={name}
          input={input}
          key={module.name}
          isClearBtnEnabled={isClearBtnEnabled}
          _privateProps={{
            hide:
              !isShowMoreFiltersActive &&
              index >= minimumModulesCount &&
              !isXsScreen,
            onClearFilterInternal: onClearFilterHandle,
          }}
        >
          {content}
        </FilterModule>
      );
    });

    const [appliedFilterTagsState, setAppliedFilterTagsState] = useState(() =>
      getAppliedFilterTagsInfo(
        filterModules,
        isControlled ? controlledValuesMap : appliedFilterState
      )
    );

    const appliedFilterTagsControlledRef = useRef(appliedFilterTagsState);

    if (isControlled && withApplyButton && updateFilterTagsRef.current) {
      appliedFilterTagsControlledRef.current = getAppliedFilterTagsInfo(
        filterModules,
        controlledValuesMap
      );
    }

    const appliedFilterTags = withApplyButton
      ? isControlled
        ? appliedFilterTagsControlledRef.current
        : appliedFilterTagsState
      : getAppliedFilterTagsInfo(
          filterModules,
          isControlled ? controlledValuesMap : appliedFilterState
        );

    return (
      <FilterContext.Provider value={{ isXsScreen }}>
        <div ref={filterRef} className={containerClasses} {...rest}>
          <FilterHeader
            filterExpanded={filterExpanded}
            resultsCount={resultsCount}
            onToggleFiltersClick={_onToggleFiltersClickHandle}
            appliedFilterTags={appliedFilterTags}
            onRemoveFilterInternal={onRemoveFilterHandle}
            onClearAllFiltersInternal={onClearAllFiltersHandle}
            renderAppliedFiltersHiddenText={renderAppliedFiltersHiddenText}
            clearAllText={clearAllText}
          />
          <div className={filterContentClasses}>
            <div className="spark-filter__modules-container">
              {filterExpanded ? modules : null}
            </div>
            <FilterFooter
              isShowMoreFiltersActive={isShowMoreFiltersActive}
              onToggleMoreFiltersClick={_onToggleMoreFiltersClickHandle}
              onApplyFiltersInternal={_onApplyFiltersButtonHandle}
              withApplyButton={withApplyButton}
              applyButtonText={applyButtonText}
              showFewFiltersCountInternal={minimumModulesCount}
              filterModulesCount={filterModules.length}
              footerButtons={footerButtons}
            />
          </div>
        </div>
      </FilterContext.Provider>
    );
  }
);

Filter.displayName = "Filter";
